import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Card, CardContent } from "@/components/ui/card"
import { Newspaper, Megaphone } from "lucide-react"
import { motion } from "framer-motion"
import { t } from "@/lib/i18n"
import { useLanguage } from "@/lib/lang"
// Hindi QA imports removed
import { TabbedNews } from "@/components/ui/TabbedNews"
import { HomeHero } from "@/components/HomeHero"
import { PanchayatSelector } from "@/components/PanchayatSelector"
import { useAuth } from "@/contexts/AuthContext"

const PERF_DEBUG_SESSION_ID = "slow-page-load"

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!perfDebugUrl) return
  fetch(perfDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PERF_DEBUG_SESSION_ID,
      runId: "pre-fix",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

export default function HaryanaPanchayatPortal() {
  const { lang, setLang } = useLanguage()
  const { lang: paramsLang } = useParams<{ lang?: string }>()
  const { currentUser } = useAuth()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const supported = ["en", "hi", "pa"] as const
    const next = supported.includes(paramsLang as any) ? (paramsLang as typeof supported[number]) : null
    if (next && next !== lang) {
      setLang(next)
    }
  }, [paramsLang, lang, setLang])
  useEffect(() => {
    if (typeof window === "undefined") return
    const update = () => setIsMobile(window.innerWidth <= 640)
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [])
  const welcomeTitle = t("welcome_title", lang)
  const [homeViewMode, setHomeViewMode] = useState<'saved' | 'selector'>(() => {
    try {
      const v = localStorage.getItem('home_view_mode')
      return v === 'saved' ? 'saved' : 'selector'
    } catch {
      return 'selector'
    }
  })
  const [savedProfileGp, setSavedProfileGp] = useState<{ district: string; block: string; village: string } | null>(null)
  // Hindi QA state removed

  useEffect(() => {
    // #region debug-point E:home-map-load-skipped
    reportPerfDebug("E", "src/components/HaryanaPanchayatPortal.tsx:308", "[DEBUG] Home page skipped eager panchayat map hydration", {
      reason: "homepage_does_not_require_full_dataset",
    })
    // #endregion
  }, [])

  useEffect(() => {
    document.title = t("title", lang)
  }, [lang])

  const loadSavedProfileGp = async (userId?: string) => {
    if (!userId) {
      try {
        const s = localStorage.getItem('user_profile')
        if (s) {
          const parsed = JSON.parse(s) as { district?: string; block?: string; village?: string }
          setSavedProfileGp({
            district: parsed.district || '',
            block: parsed.block || '',
            village: parsed.village || '',
          })
        }
      } catch {
      }
      return
    }
    return
  }

  useEffect(() => {
    const onMode = (e: Event) => {
      const ev = e as CustomEvent<{ mode?: 'saved' | 'selector' }>
      if (ev.detail?.mode === 'saved' || ev.detail?.mode === 'selector') {
        setHomeViewMode(ev.detail.mode)
      }
    }
    window.addEventListener('home:view-mode', onMode as EventListener)
    return () => {
      window.removeEventListener('home:view-mode', onMode as EventListener)
    }
  }, [])

  useEffect(() => {
    try {
      const v = localStorage.getItem('home_view_mode')
      setHomeViewMode(v === 'saved' ? 'saved' : 'selector')
    } catch {
    }
  }, [])

  useEffect(() => {
    loadSavedProfileGp(currentUser?.id)
    const onProfileUpdated = () => {
      loadSavedProfileGp(currentUser?.id)
    }
    window.addEventListener('profile:updated', onProfileUpdated)
    return () => {
      window.removeEventListener('profile:updated', onProfileUpdated)
    }
  }, [currentUser?.id])

  const savedGpLine = savedProfileGp?.village && savedProfileGp?.block && savedProfileGp?.district
    ? `${savedProfileGp.village}, ${savedProfileGp.block}, ${savedProfileGp.district}`
    : null

  // Language initialization and persistence handled globally

  // Hindi QA logging removed

  // Hindi QA action removed

  // Selection controls removed; dependent memoized options and handlers deleted to reduce unused code

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-emerald-100" lang={lang}>
      {/* Header moved to global SiteHeader */}

      <HomeHero />

      {/* State Overview */}
      <section className="p-6 text-center">
        <h2
          className={[
            "welcome-title-fluid font-bold text-green-800 mb-4 text-center",
            isMobile ? "leading-[1.2]" : "text-3xl leading-[1.32]",
          ].join(" ")}
        >
          <span className="inline-block whitespace-nowrap">{welcomeTitle}</span>
        </h2>
        <p className="text-gray-700 max-w-3xl mx-auto mb-6">
          {t("welcome_body", lang)}
        </p>

        {/* Selection controls removed per current requirements */}



        {/* Secondary Banner */}
        <div className="banner-secondary mt-6 md:mt-10 py-4">
          <h2 className="text-xl md:text-2xl font-bold text-center">
            {t("level_header_tagline", lang)}
          </h2>
        </div>

        {homeViewMode === 'saved' && (
          <div className="px-6 mb-4">
            <div className="max-w-6xl mx-auto">
              <div className="border rounded-md p-4 bg-white shadow-sm">
                <div className="text-xs text-gray-600 mb-1">Home Gram Panchayat (saved in profile)</div>
                <div className="text-lg font-semibold text-green-800">{savedGpLine || 'Not Set'}</div>
                <div className="text-xs text-gray-500 mt-1">Press the Home icon again to change.</div>
              </div>
            </div>
          </div>
        )}

        <PanchayatSelector
          mode="navigate"
          lockedGp={null}
          storageKey="home_gp_selection"
        />

        {/* Hindi QA summary removed */}
      </section>

      {/* Latest Updates Title */}
      <div className="home-updates-ad-rail">
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-2xl md:text-3xl font-bold text-green-800 flex items-center gap-2">
            <Newspaper /> {t("latest_updates_title", lang)}
          </h2>
        </div>
        <div className="border-b border-green-200 mb-4" />
      </div>

      {/* Notification & Advertisement Section */}
      <section className="pb-6">
        <div className="home-updates-ad-rail home-updates-ad-grid">
          <motion.div whileHover={{ scale: 1.02 }} className="home-updates-ad-box">
            <TabbedNews lang={lang} />
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }} className="home-updates-ad-box">
            <Card className="bg-yellow-100 shadow-lg">
              <CardContent className="p-4 text-center">
                <h3 className="text-lg font-bold text-gray-800 flex items-center justify-center gap-2 mb-2"><Megaphone /> {t("ad_title", lang)}</h3>
                <p className="text-gray-600">
                  {t("ad_body", lang)}
                  {" "}
                  <a
                    href="mailto:techmaestrodigital@gmail.com"
                    className="font-medium underline underline-offset-2 hover:text-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 focus-visible:ring-offset-yellow-100 rounded"
                  >
                    techmaestrodigital@gmail.com
                  </a>
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Haryana Overview */}
      <section className="bg-green-50 py-10">
        <div className={["max-w-5xl text-center", isMobile ? "mx-4" : "mx-auto px-4 sm:px-0"].join(" ")}>
          <h3 className="text-2xl font-bold text-green-800 mb-4">{t("dashboard_title", lang)}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-gray-800">
            {/* 1. Zila Parishad */}
            <Card className={`shadow-md ${isMobile ? "w-3/4 mx-auto" : ""}`}>
              <CardContent className={`${isMobile ? "p-3 h-[96px] flex flex-col items-center justify-center" : "p-4"} text-center`}>
                <h4 className="font-semibold">{t("level_zp", lang)}</h4>
                <p className="text-2xl font-bold text-green-700">22</p>
              </CardContent>
            </Card>
            {/* 2. Block Samiti */}
            <Card className={`shadow-md ${isMobile ? "w-3/4 mx-auto" : ""}`}>
              <CardContent className={`${isMobile ? "p-3 h-[96px] flex flex-col items-center justify-center" : "p-4"} text-center`}>
                <h4 className="font-semibold">{t("block_samitis", lang)}</h4>
                <p className="text-2xl font-bold text-green-700">143</p>
              </CardContent>
            </Card>
            {/* 3. Gram Panchayat */}
            <Card className={`shadow-md ${isMobile ? "w-3/4 mx-auto" : ""}`}>
              <CardContent className={`${isMobile ? "p-3 h-[96px] flex flex-col items-center justify-center" : "p-4"} text-center`}>
                <h4 className="font-semibold">{t("level_gp", lang)}</h4>
                <p className="text-2xl font-bold text-green-700">6,222</p>
              </CardContent>
            </Card>
            {!isMobile ? (
              <Card className="shadow-md">
                <CardContent className="p-4 text-center">
                  <h4 className="font-semibold">{t("registered_users", lang)}</h4>
                  <p className="text-2xl font-bold text-green-700">5.2L+</p>
                </CardContent>
              </Card>
            ) : null}
          </div>
          <p className="text-[15px] text-gray-600 mt-3">
            <span className="font-semibold">{t("data_source_label", lang)}:</span> {t("data_source_value", lang)}
          </p>
        </div>
      </section>

      {/* Footer moved to global SiteFooter */}
    </div>
  )
}
